// 통합앱 자동 업데이트(docs/설계/배포_자동업데이트_설계.md §5).
//
// 정책 — **알림 후 사용자 승인**(무음 자동 설치가 아니다):
//   업데이트 설치는 앱 재시작을 수반한다. 원격지원 도중 재시작하면 지원이 그 자리에서 끊기므로,
//   언제 재시작할지는 사람이 정해야 한다. (무인 서비스 에이전트는 반대로 무음 자동이다 — 승인해 줄
//   사람이 없기 때문. 같은 제품 안에서 두 정책이 공존하는 이유가 이것이다.)
//
// 이 모듈은 **웹(브라우저)에서도 안전하게 import 된다** — 플러그인은 동적 import 라 껍데기가 아니면
// 아예 로드되지 않는다. 웹 뷰어는 서버가 주는 최신 정적 자산을 받으므로 자체 업데이트 개념이 없다.

import { IS_SHELL } from './shell';

/** 확인된 업데이트 정보(화면 표시에 필요한 만큼만). */
export interface AvailableUpdate {
  version: string;
  notes: string;
  /** 실제 설치를 수행한다(다운로드 → 설치 → 재시작). */
  install: () => Promise<void>;
}

/**
 * 새 버전이 있는지 확인한다. 없거나 확인할 수 없으면 null.
 *
 * ⚠️ **실패를 사용자에게 노출하지 않는다.** 업데이트 확인은 부가 기능이라, 서버가 잠시 죽었다고
 * 원격지원 화면에 오류를 띄우면 본래 작업을 방해할 뿐이다. 조용히 넘어가고 다음 기동에서 다시 시도한다.
 */
export async function checkForUpdate(): Promise<AvailableUpdate | null> {
  // 브라우저(순수 웹)에서는 업데이트 개념이 없다 → 플러그인을 로드조차 하지 않는다.
  if (!IS_SHELL) {
    return null;
  }
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update) {
      return null; // 서버가 204 = 최신
    }
    return {
      version: update.version,
      notes: update.body ?? '',
      install: async () => {
        await update.downloadAndInstall();
        // ⚠️⚠️ **재시작 방식이 플랫폼마다 다르다. 아래 `relaunch()` 는 macOS 전용이다.**
        //
        //  - **Windows**: 여기로 돌아오지 않는다. 플러그인이 설치관리자를 띄운 **직후
        //    `std::process::exit(0)` 로 이 프로세스를 끝낸다**(tauri-plugin-updater `install_inner`).
        //    재시작은 NSIS 가 맡는다 — 업데이터가 `/P /UPDATE /R` 로 실행하고, 설치관리자의
        //    `.onInstSuccess` 가 `/R` 을 보고 새 실행파일을 다시 띄운다.
        //    ⇒ **여기에 Windows 용 재시작 코드를 더 넣으려 하지 말 것.** 도달하지 않는다.
        //    (`/R` 이 듣지 않는다면 원인은 이 파일이 아니라 `installer-hooks.nsh` 다 —
        //     설치 훅이 대화상자를 띄우면 그만큼 재시작이 늦어지거나 멈춘다. 그 주석 참조.)
        //  - **macOS**: 플러그인이 app 번들만 교체하고 **프로세스는 살아 있다.** 그래서 이 줄이 필요하다.
        //    이것을 "Windows 에서 안 불리니 죽은 코드" 로 보고 지우면 **Mac 만 조용히 갱신 안 된 채 남는다.**
        const { relaunch } = await import('@tauri-apps/plugin-process');
        await relaunch();
      },
    };
  } catch (e) {
    // 네트워크 실패·엔드포인트 오류·서명 검증 실패 모두 여기로 온다.
    // 서명 검증 실패는 보안상 중요한 신호지만, 사용자가 할 수 있는 일이 없으므로 콘솔에만 남긴다.
    console.warn('[update] 확인 실패(무시):', e);
    return null;
  }
}
